import imageCompression from "browser-image-compression";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchReturnableOrder } from "@repo/shopify/api/fetchReturnableOrder";
import { requestReturn as requestReturnApi } from "@repo/shopify/api/requestReturn";
import { uploadS3 } from "@repo/shopify/api/uploadS3";
import type { IOrder, IReturnableFulfillmentLineItem } from "@repo/shopify/types/api/return";
import { ReturnReason } from "@repo/shopify/types/enum";
import { getShippingFeeIntl, getShippingFeeKr, processGiftReturnItems } from "@repo/shopify/utils/return";

import intlAddresses from "@assets/intl_addresses.json";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import ReturnLineItem from "@molecules/ReturnLineItem";

import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import useCustomer from "@queries/useCustomer";
import useMetaObjects from "@queries/useMetaObjects";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export interface IReturnRequestedItem {
  id: string;
  quantity: number;
  isCheck: boolean;
  image: File[] | null;
  returnReason: ReturnReason;
  customerNote: string;
  cost: number;
  weight?: { value: number; unit: string };
}

const DEFAULT_SHIPPING_FEE = 5000;
const COMPRESS_FILE_SIZE_MB = 3;
const COMPRESS_FILE_MAX_WIDTH_OR_HEIGHT = 1024;

interface IState {
  orderId: string;
}

export default function ReturnRequest() {
  const { setMeta, openModal, openToast, showFooter } = useLayoutStore();
  const { customer, token } = useCustomerStore();
  const { fetchCustomer } = useCustomer();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();

  const { fetchMetaObjectType: fetchGiftByPrice, metaObjectTypeData: giftByPrice } = useMetaObjects();

  /** 메타오브젝트에서 Gift By Products 받아오기 */
  const { fetchMetaObjectType: fetchGiftByProduct, metaObjectTypeData: giftByProduct } = useMetaObjects();

  /** 주문 정보를 관리하는 state */
  const [order, setOrder] = useState<IOrder | undefined>(undefined);
  /** 반품 가능한 상품(사은품 제외)을 관리하는 state */
  const [returnableItems, setReturnableItems] = useState<IReturnableFulfillmentLineItem[] | undefined>(undefined);
  /** 반품 가능한 사은품을 관리하는 state */
  const [returnableGiftItems, setReturnableGiftItems] = useState<IReturnableFulfillmentLineItem[] | undefined>(undefined);
  /** 반품할 상품(사은품 제외)을 관리하는 state */
  const [returnRequestItems, setReturnRequestItems] = useState<IReturnRequestedItem[] | undefined>(undefined);
  /** 반품할 사은품을 관리하는 state */
  const [returnRequestGiftItems, setReturnRequestGiftItems] = useState<IReturnRequestedItem[] | undefined>(undefined);
  /** 제출 버튼 로딩 상태를 관리하는 state */
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  /** 주문 내역 중 삭제된 상품이 있는지 여부를 확인하는 state */
  const [hasDeletedProduct, setHasDeletedProduct] = useState(false);
  /** 반품 배송비를 관리하기 위한 state */
  const [returnShippingFee, setReturnShippingFee] = useState(0);
  /** 반품 배송비를 불러오는 동안의 로딩 상태를 관리하기 위한 state */
  const [isLoadingReturnShippingFee, setIsLoadingReturnShippingFee] = useState(true);

  // 반품 신청 버튼을 클릭 했을 경우만, 접근 가능하도록 설정
  const orderId = location.state ? (location.state as IState).orderId : null;

  useEffect(() => {
    setMeta({ title: `Return Request - ${DEFAULT_TITLE}` });
    showFooter(false);
    window.scrollTo({ top: 0 });

    void fetchGiftByPrice({ type: "gift_by_price", first: 10, reverse: true });
    void fetchGiftByProduct({ type: "gift_by_product", first: 10, reverse: true });
  }, []);

  useEffect(() => {
    if (!orderId) navigate(URLS.NOT_FOUND);
    else void getReturnableOrder();
  }, [orderId]);

  // 주문내역중에 하나라도 삭제된 상품이 있는 경우, Alert 모달 노출
  useEffect(() => {
    if (hasDeletedProduct) {
      openModal({
        title: "Alert",
        content: <p>{t("no_returnable_product")}</p>,
        confirm: {
          label: "Confirm",
          onClick: () => {
            openModal(null);
          },
        },
        onClickClose: () => {
          openModal(null);
        },
      });
    }
  }, [hasDeletedProduct]);

  useEffect(() => {
    if (!returnRequestItems || !order) {
      setIsLoadingReturnShippingFee(true);
      return;
    }

    // 체크한 상품 배열
    const checkedLineItems = returnRequestItems.filter((item) => item.isCheck);
    // 체크하지 않은 상품 배열
    const remainingLineItems = returnableItems
      ?.map((returnableItem) => {
        const returnRequestedItem = returnRequestItems?.find(
          (returnRequestItem) => returnRequestItem.id === returnableItem.fulfillmentLineItem.id,
        );
        // 모든 수량이 다 체크되면 remainingLineItems 에서 제외
        if (returnRequestedItem?.isCheck && returnRequestedItem?.quantity === returnableItem.quantity) return;
        return {
          ...returnableItem,
          quantity: returnableItem.quantity - (returnRequestedItem?.isCheck ? returnRequestedItem?.quantity : 0), // 남은 수량 계산
        };
      })
      .filter(Boolean) as IReturnableFulfillmentLineItem[];

    // 조건 확인 후 자동 체크된 사은품 배열
    const checkedGiftLineItems = processGiftReturnItems({
      returnableGiftItems: returnableGiftItems ?? [],
      remainingLineItems,
      /** 현재 active 상태의 사은품 메타오브젝트만 전달됨. */
      giftByPrice: giftByPrice?.metaobjects?.edges?.flatMap(({ node }) => node) || null,
      giftByProduct: giftByProduct?.metaobjects?.edges?.flatMap(({ node }) => node) || null,
    });

    // 사은품 반품 상태 업데이트
    setReturnRequestGiftItems(checkedGiftLineItems);

    if (!checkedLineItems.length) {
      setIsLoadingReturnShippingFee(true);
      return;
    }

    const requestReturnLineItems = checkedLineItems.map(({ id, returnReason, quantity, customerNote }) => ({
      fulfillmentLineItemId: id,
      returnReason,
      quantity,
      customerNote,
    }));

    // `returnReason` 값이 빈 문자열인 경우 로딩 상태 유지
    const hasReturnReason = requestReturnLineItems.every((item) => item.returnReason && item.returnReason !== ReturnReason.DEFAULT);

    if (!hasReturnReason) {
      setIsLoadingReturnShippingFee(true);
      return;
    }

    // 주문서의 총 상품의 수
    const totalOrderItemCount = order.totalOrderItemCount;

    // 반품(신청 / 승인)된 상품 수
    const totalReturnedItemCount = order.totalReturnedItemCount;

    // 반품 신청할 상품의 수
    const totalReturnRequestNonGiftItemCount = checkedLineItems?.reduce((total, item) => total + item.quantity, 0) || 0;

    // 반품 신청할 사은품의 수
    const totalReturnRequestGiftItemCount = checkedGiftLineItems?.reduce((total, item) => total + item.quantity, 0) || 0;

    // 반품 신청할 총 아이템 개수
    const totalReturnRequestItemCount = totalReturnRequestNonGiftItemCount + totalReturnRequestGiftItemCount;

    // 반품사유가 DEFECTIVE, WRONG_ITEM인 경우 반품 배송비 무료 ( + 직원계정 )
    const isFreeReturnShipping = requestReturnLineItems.some(
      (item) =>
        item.returnReason === ReturnReason.DEFECTIVE || item.returnReason === ReturnReason.WRONG_ITEM || customer.tags.includes("Employee"),
    );

    const _returnShippingFee =
      lang === "ko"
        ? getShippingFeeKr({
            isFreeReturnShipping,
            totalOrderItemCount,
            totalReturnedItemCount,
            totalReturnRequestItemCount,
            shippingFee: DEFAULT_SHIPPING_FEE,
          })
        : getShippingFeeIntl({
            checkedLineItems,
            country: order.country,
            isFreeReturnShipping,
            totalOrderItemCount,
            totalReturnedItemCount,
            totalReturnRequestItemCount,
            addresses: intlAddresses,
            defaultBoxWeight: 1.5,
          });

    setReturnShippingFee(_returnShippingFee);
    setIsLoadingReturnShippingFee(false);
  }, [returnRequestItems]);

  /** 반품 신청이 가능한 데이터를 가져오는 함수 */
  const getReturnableOrder = async () => {
    if (orderId) {
      try {
        const response = await fetchReturnableOrder({
          storeName: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
          domain: lang === "ko" ? "kr" : "intl",
          orderId,
          customerId: customer.id,
        });

        if (!response) {
          openToast(NOTIFICATIONS.ERROR_NO_RETURN_PRODUCT);
          navigate(-1);
          return;
        }

        // 주문 내역
        const fulfillmentOrder = response.order;

        const returnableFulfillmentLineItems = response.returnableFulfillmentLineItems;

        const hasNullVariant =
          returnableFulfillmentLineItems.some((item) => item.fulfillmentLineItem.lineItem.variant === null) ||
          returnableFulfillmentLineItems.some((item) => item.fulfillmentLineItem.lineItem.product === null);

        setHasDeletedProduct(hasNullVariant);

        // 상품과 사은품 분리
        const returnableLineItems = returnableFulfillmentLineItems.filter(
          (returnableFulfillmentLineItem) => !returnableFulfillmentLineItem.fulfillmentLineItem.lineItem.product?.tags.includes("Gift"),
        );
        const returnableGiftLineItems = returnableFulfillmentLineItems.filter((returnableFulfillmentLineItem) =>
          returnableFulfillmentLineItem.fulfillmentLineItem.lineItem.product?.tags.includes("Gift"),
        );

        setReturnableItems(returnableLineItems);
        setReturnableGiftItems(returnableGiftLineItems);

        setOrder(fulfillmentOrder);
      } catch (error) {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        navigate(-1);
      }
    }
  };

  // 반품 신청 데이터 상태를 업데이트하는 함수
  const updateReturnRequestItem = (id: string, updatedFields: Partial<IReturnRequestedItem>) => {
    setReturnRequestItems((prevItems) => {
      const requestedItems = returnableItems
        ?.filter((returnableItem) => returnableItem.fulfillmentLineItem.id === id)
        .map((returnableItem) => {
          const originalTotalSet = parseFloat(returnableItem.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
          const calculatedDiscountAmount = returnableItem.fulfillmentLineItem.lineItem.discountAllocations
            .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
            .reduce((a, b) => a + b, 0);

          const discountedTotalSet = (originalTotalSet - calculatedDiscountAmount) / (returnableItem?.quantity ?? 1);

          return {
            id: returnableItem.fulfillmentLineItem.id,
            quantity: updatedFields.quantity ?? 1, // 기본값 1
            isCheck: updatedFields.isCheck ?? false, // 기본값 false
            image: null, // 파손된 상품의 경우, 반품 사유 이미지
            returnReason: updatedFields.returnReason ?? ReturnReason.DEFAULT, // 반품 사유
            customerNote: updatedFields.customerNote ?? "", // 반품 상세 사유
            cost: discountedTotalSet, // 반품 상품 결제 금액
            ...(lang === "en" &&
              returnableItem.fulfillmentLineItem.weight && {
                weight: {
                  value: returnableItem.fulfillmentLineItem.weight.value ?? 0,
                  unit: returnableItem.fulfillmentLineItem.weight.unit,
                },
              }),
          };
        });

      // returnRequestItems가 존재하지 않으면 초기화
      if (!prevItems) {
        return requestedItems || []; // prevItems가 없으면 새 아이템들 반환
      }

      // prevItems가 존재하면 해당 id에 맞는 항목만 업데이트하고, 없으면 새 항목 추가
      const updatedItems = prevItems.map((item) => (item.id === id ? { ...item, ...updatedFields } : item));

      // prevItems에 해당 id가 없다면, requestedItems에서 새 아이템을 추가
      if (!prevItems.some((item) => item.id === id)) {
        if (requestedItems) {
          updatedItems.push(...requestedItems);
        }
      }

      return updatedItems;
    });
  };

  /** 반품신청할 상품들의 유효성 검사*/
  const validateRequestData = (checkedLineItems: IReturnRequestedItem[]) => {
    // gift(returnReason !== "OTHER")를 제외한 상품들만 대상으로 validate
    const checkedNonGiftLineItems = checkedLineItems.filter((item) => item.isCheck && item.returnReason !== ReturnReason.OTHER);

    // 체크된 상품이 존재하지 않는 경우
    if (!checkedNonGiftLineItems.length) {
      openToast(NOTIFICATIONS.WARNING_NO_RETURN_PRODUCTS);
      return false;
    }

    // 반품사유가 선택되지 않은 경우
    if (
      !checkedNonGiftLineItems.every((item) => item.returnReason && item.returnReason !== ReturnReason.DEFAULT) ||
      isLoadingReturnShippingFee
    ) {
      openToast(NOTIFICATIONS.WARNING_NO_REASON);
      return false;
    }

    // 반품사유가 자사 귀책인데 이미지를 첨부하지 않은 경우
    if (
      checkedNonGiftLineItems.some(
        (item) => (item.returnReason === ReturnReason.DEFECTIVE || item.returnReason === ReturnReason.WRONG_ITEM) && !item.image,
      )
    ) {
      openToast(NOTIFICATIONS.WARNING_NO_IMAGE);
      return false;
    }

    // 반품사유가 자사 귀책인데 상세사유를 첨부하지 않은 경우
    if (
      checkedNonGiftLineItems.some(
        (item) => (item.returnReason === ReturnReason.DEFECTIVE || item.returnReason === ReturnReason.WRONG_ITEM) && !item.customerNote,
      )
    ) {
      openToast(NOTIFICATIONS.WARNING_NO_DETAIL_REASON);
      return false;
    }

    return true;
  };

  /** requestReturn lambda API 호출 */
  const requestReturn = async () => {
    // 반품 신청할 상품이 없는 경우
    if (!returnableItems || !returnRequestItems || !order) {
      openToast(NOTIFICATIONS.WARNING_NO_RETURN_PRODUCTS);
      return;
    }

    // 1. 상품과 사은품 배열 합치기
    const mergedLineItems = returnRequestItems.concat(returnRequestGiftItems || []);

    // 2. 체크되지 않은 상품 제외
    const checkedLineItems = mergedLineItems.filter((item) => item.isCheck);

    // 3. 유효성 검사
    const checkValidate = validateRequestData(checkedLineItems);
    if (!checkValidate) {
      throw new Error("반품 신청 데이터 유효성 검사 실패");
    }

    // 모든 체크된 상품들의 이미지들이 병합된 File[] 배열
    const requestImageData = checkedLineItems
      .filter((item) => item.image)
      .reduce<File[]>((acc: File[], cur: IReturnRequestedItem) => {
        if (cur.image) return [...acc, ...cur.image];
        return acc;
      }, []);

    const requestReturnLineItems = checkedLineItems.map(({ id, returnReason, quantity, customerNote }) => ({
      fulfillmentLineItemId: id,
      returnReason,
      quantity,
      customerNote,
    }));

    try {
      setIsSubmitLoading(true);

      // 압축된 이미지 파일들에 대한 배열
      const compressedFiles = await Promise.all(
        // requestImageData 배열의 각 이미지 파일을 압축하는 작업
        // 모든 압축 작업이 완료된 후 결과를 배열로 반환
        requestImageData.map((image) =>
          imageCompression(image, {
            maxSizeMB: COMPRESS_FILE_SIZE_MB, // 압축 후 최대 파일 크기(MB)
            maxWidthOrHeight: COMPRESS_FILE_MAX_WIDTH_OR_HEIGHT, // 압축 후 최대 너비 또는 높이(픽셀)
            useWebWorker: true, // 웹 워커를 사용하여 비동기적으로 이미지 압축
          }),
        ),
      );

      const returnImageUrls = compressedFiles.length ? await uploadS3(`${import.meta.env.VITE_REACT_APP_SHOP_NAME}`, compressedFiles) : [];

      const res = await requestReturnApi({
        shop: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
        domain: lang === "ko" ? "kr" : "intl",
        orderId: orderId || "",
        returnLineItems: requestReturnLineItems,
        returnShippingFee,
        returnImageUrls,
      });

      if (res) {
        // 동시 신청으로 이미 반품한 상품
        const hasQuantityError = res.userErrors?.some((error) => error?.field?.includes("quantity"));
        if (hasQuantityError) {
          openModal({
            title: "Alert",
            content: <p>{t("no_quantity_product")}</p>,
            confirm: {
              label: "Confirm",
              onClick: () => {
                openModal(null);
              },
            },
            onClickClose: () => {
              openModal(null);
            },
          });

          return;
        }

        void fetchCustomer({ customerAccessToken: token.accessToken });
        // RETURN REQUESTED 페이지로 이동
        navigate(`${URLS.RETURN_REQUESTED}/${orderId}`, {
          state: {
            returnId: res.returnId,
            returnRefundLineItems: res.returnRefundLineItems,
            refundDuties: res.refundDuties,
            returnShippingFee,
          },
        });
      }
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_SERVER);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  return (
    <>
      {!order || !returnableItems ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
          <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-5 leading-none">
            <h2 className="pc:text-[1.125rem] text-lg">Return Request ({order.name || ""})</h2>
            <div className="pc:gap-2.5 flex flex-col gap-2">
              <p className="text-base leading-5">
                <Trans i18nKey="return.description" />
              </p>
              <Link as="square" className="flex! items-center justify-center px-3!" href={URLS.SHIPPING_AND_RETURNS} variant="secondary">
                {t("return.view_policy")}
              </Link>
            </div>
          </section>

          <section className="pc:col-span-5 pc:col-start-4 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10 leading-none">
            {/* Return Line Items */}
            <ul className="pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid gap-y-10">
              {returnableItems?.map((returnableItem) => {
                const originalTotalSet = parseFloat(returnableItem.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
                const calculatedDiscountAmount = returnableItem.fulfillmentLineItem.lineItem.discountAllocations
                  .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
                  .reduce((a, b) => a + b, 0);

                // TODO: 추후 프론트단에서 계산하지말고 람다에서 계산된 값을 반환해주도록 수정할 예정
                const discountedTotalSet = originalTotalSet - calculatedDiscountAmount;

                return (
                  <ReturnLineItem
                    compareAtPrice={discountedTotalSet.toString()}
                    currentStep="request"
                    disabled={!returnableItem.fulfillmentLineItem.lineItem.variant}
                    id={returnableItem.fulfillmentLineItem.id}
                    imageUrl={
                      returnableItem.fulfillmentLineItem.lineItem.image ? returnableItem.fulfillmentLineItem.lineItem.image.url : ""
                    }
                    isGift={false}
                    key={returnableItem.fulfillmentLineItem.id}
                    onChangeReturnRequestItems={updateReturnRequestItem}
                    originalQuantity={returnableItem.fulfillmentLineItem.quantity}
                    price={discountedTotalSet.toString()}
                    returnQuantity={returnableItem.quantity}
                    returnRequestItem={returnRequestItems?.find(
                      (returnRequestItem) => returnRequestItem.id === returnableItem.fulfillmentLineItem.id,
                    )}
                    size={
                      returnableItem.fulfillmentLineItem.lineItem.variant
                        ? returnableItem.fulfillmentLineItem.lineItem.variant.selectedOptions.find((option) => option.name === "Size")
                            ?.value || ""
                        : (/\/\s*(?<number>\d+)$/.exec(returnableItem.fulfillmentLineItem.lineItem.variantTitle || "") || [])[1]
                    }
                    title={returnableItem.fulfillmentLineItem.lineItem.title}
                    variantTitle={returnableItem.fulfillmentLineItem.lineItem.variantTitle}
                  />
                );
              })}
              {returnableGiftItems?.map((returnableItem) => {
                const originalTotalSet = parseFloat(returnableItem.fulfillmentLineItem.originalTotalSet.shopMoney.amount);
                const calculatedDiscountAmount = returnableItem.fulfillmentLineItem.lineItem.discountAllocations
                  .map(({ allocatedAmount }) => parseFloat(allocatedAmount.amount))
                  .reduce((a, b) => a + b, 0);

                const discountedTotalSet = originalTotalSet - calculatedDiscountAmount;

                return (
                  <ReturnLineItem
                    compareAtPrice={discountedTotalSet.toString()}
                    currentStep="request"
                    disabled={!returnableItem.fulfillmentLineItem.lineItem.variant}
                    id={returnableItem.fulfillmentLineItem.id}
                    imageUrl={
                      returnableItem.fulfillmentLineItem.lineItem.image ? returnableItem.fulfillmentLineItem.lineItem.image.url : ""
                    }
                    isGift
                    key={returnableItem.fulfillmentLineItem.id}
                    originalQuantity={returnableItem.fulfillmentLineItem.quantity}
                    price={discountedTotalSet.toString()}
                    returnQuantity={returnableItem.quantity}
                    returnRequestItem={returnRequestGiftItems?.find(
                      (returnRequestGiftItem) => returnRequestGiftItem.id === returnableItem.fulfillmentLineItem.id,
                    )}
                    size={
                      returnableItem.fulfillmentLineItem.lineItem.variant
                        ? returnableItem.fulfillmentLineItem.lineItem.variant.selectedOptions.find((option) => option.name === "Size")
                            ?.value || ""
                        : (/\/\s*(?<number>\d+)$/.exec(returnableItem.fulfillmentLineItem.lineItem.variantTitle || "") || [])[1]
                    }
                    title={returnableItem.fulfillmentLineItem.lineItem.title}
                    variantTitle={returnableItem.fulfillmentLineItem.lineItem.variantTitle}
                  />
                );
              })}
            </ul>

            <Button
              as="square"
              className="pc:col-span-5 col-span-8 w-full!"
              disabled={(hasDeletedProduct && returnableItems?.length === 1) || isSubmitLoading}
              loading={isSubmitLoading}
              onClick={() => {
                void requestReturn();
              }}
            >
              {t("return.request")}
            </Button>
          </section>
        </section>
      )}
    </>
  );
}
